import { apiClient } from '@/lib/apiClient';
import type {
  Venda,
  VendaItem,
  VendaPagamento,
  ProdutoPdv,
  ClientePdv,
  NfceDados,
  EstornoResultado,
  EstornoResumoItem,
  EstornoErroItem,
  EstornoPendencia,
  DevolucaoResultado,
  AbrirVendaDto,
  AdicionarItemDto,
  AlterarQuantidadeDto,
  AplicarDescontoDto,
  AdicionarPagamentoDto,
  Turno,
  TurnoResumo,
  AbrirTurnoDto,
  FecharTurnoDto,
  MovimentoCaixaDto,
  VendaOfflineDto,
  SyncOfflineResultado,
} from '../types/pdv.types';

const BASE = '/api/pdv';

export const pdvService = {
  async obterVenda(id: number): Promise<Venda> {
    const { data } = await apiClient.get<Venda>(`${BASE}/${id}`);
    return data;
  },

  async abrirVenda(dto: AbrirVendaDto): Promise<Venda> {
    const { data } = await apiClient.post<Venda>(`${BASE}/abrir`, dto);
    return data;
  },

  async adicionarItem(vendaId: number, dto: AdicionarItemDto): Promise<VendaItem> {
    const { data } = await apiClient.post<VendaItem>(`${BASE}/${vendaId}/item`, dto);
    return data;
  },

  async alterarQuantidade(vendaId: number, itemId: number, dto: AlterarQuantidadeDto): Promise<VendaItem> {
    const { data } = await apiClient.put<VendaItem>(`${BASE}/${vendaId}/item/${itemId}`, dto);
    return data;
  },

  async removerItem(vendaId: number, itemId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${vendaId}/item/${itemId}`);
  },

  async aplicarDesconto(vendaId: number, dto: AplicarDescontoDto): Promise<Venda> {
    const { data } = await apiClient.post<Venda>(`${BASE}/${vendaId}/desconto`, dto);
    return data;
  },

  async adicionarPagamento(vendaId: number, dto: AdicionarPagamentoDto): Promise<VendaPagamento> {
    const { data } = await apiClient.post<VendaPagamento>(`${BASE}/${vendaId}/pagamento`, dto);
    return data;
  },

  async removerPagamento(vendaId: number, pagamentoId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${vendaId}/pagamento/${pagamentoId}`);
  },

  async finalizar(vendaId: number): Promise<Venda> {
    const { data } = await apiClient.post<Venda>(`${BASE}/${vendaId}/finalizar`);
    return data;
  },

  async cancelar(vendaId: number): Promise<Venda> {
    const { data } = await apiClient.post<Venda>(`${BASE}/${vendaId}/cancelar`);
    return data;
  },

  async associarCliente(vendaId: number, clienteId: number): Promise<Venda> {
    const { data } = await apiClient.put<Venda>(`${BASE}/${vendaId}/cliente`, { clienteId });
    return data;
  },

  async estornar(vendaId: number, motivo: string): Promise<EstornoResultado> {
    const { data } = await apiClient.post<EstornoResultado>(`${BASE}/${vendaId}/estornar`, { motivo });
    return data;
  },

  async obterNfce(vendaId: number): Promise<NfceDados> {
    const { data } = await apiClient.get<NfceDados>(`${BASE}/${vendaId}/nfce`);
    return data;
  },

  async listarEstornos(empresaId: number, de?: string, ate?: string): Promise<EstornoResumoItem[]> {
    const { data } = await apiClient.get<EstornoResumoItem[]>(`${BASE}/estornos`, { params: { empresaId, de, ate } });
    return data;
  },

  async listarErrosEstorno(empresaId: number, de?: string, ate?: string): Promise<EstornoErroItem[]> {
    const { data } = await apiClient.get<EstornoErroItem[]>(`${BASE}/estornos/erros`, { params: { empresaId, de, ate } });
    return data;
  },

  // ── PDV-07 — devolução fiscal de estorno fora do prazo ───────────────────

  async listarPendenciasEstorno(): Promise<EstornoPendencia[]> {
    const { data } = await apiClient.get<EstornoPendencia[]>(`${BASE}/estornos/pendencias`);
    return data;
  },

  async emitirDevolucao(vendaId: number, motivo?: string): Promise<DevolucaoResultado> {
    const { data } = await apiClient.post<DevolucaoResultado>(`${BASE}/${vendaId}/devolucao`, { motivo });
    return data;
  },

  async buscarProduto(q: string): Promise<ProdutoPdv[]> {
    const { data } = await apiClient.get<ProdutoPdv[]>(`${BASE}/buscar-produto`, { params: { q } });
    return data;
  },

  async buscarCliente(q: string): Promise<ClientePdv[]> {
    const { data } = await apiClient.get<ClientePdv[]>(`${BASE}/buscar-cliente`, { params: { q } });
    return data;
  },

  // ── Turno (T2.3) ─────────────────────────────────────────────────────────

  async abrirTurno(dto: AbrirTurnoDto): Promise<Turno> {
    const { data } = await apiClient.post<Turno>(`${BASE}/turno/abrir`, dto);
    return data;
  },

  async fecharTurno(turnoId: number, dto: FecharTurnoDto): Promise<TurnoResumo> {
    const { data } = await apiClient.post<TurnoResumo>(`${BASE}/turno/${turnoId}/fechar`, dto);
    return data;
  },

  async sangria(turnoId: number, dto: MovimentoCaixaDto): Promise<MovimentoCaixaDto> {
    const { data } = await apiClient.post<MovimentoCaixaDto>(`${BASE}/turno/${turnoId}/sangria`, dto);
    return data;
  },

  async suprimento(turnoId: number, dto: MovimentoCaixaDto): Promise<MovimentoCaixaDto> {
    const { data } = await apiClient.post<MovimentoCaixaDto>(`${BASE}/turno/${turnoId}/suprimento`, dto);
    return data;
  },

  async obterTurnoAtual(): Promise<Turno | null> {
    const resp = await apiClient.get<Turno>(`${BASE}/turno/atual`);
    if (resp.status === 204) return null;
    return resp.data;
  },

  async obterResumoTurno(turnoId: number): Promise<TurnoResumo> {
    const { data } = await apiClient.get<TurnoResumo>(`${BASE}/turno/${turnoId}/resumo`);
    return data;
  },

  // ── Offline (T2.6) ───────────────────────────────────────────────────────

  async obterProdutosFrequentes(empresaId: number, limite = 200): Promise<ProdutoPdv[]> {
    const { data } = await apiClient.get<ProdutoPdv[]>(`${BASE}/produtos-frequentes`, {
      params: { empresaId, limite },
    });
    return data;
  },

  async obterClientesRecentes(empresaId: number, limite = 100): Promise<ClientePdv[]> {
    const { data } = await apiClient.get<ClientePdv[]>(`${BASE}/clientes-recentes`, {
      params: { empresaId, limite },
    });
    return data;
  },

  async syncOffline(vendas: VendaOfflineDto[]): Promise<SyncOfflineResultado> {
    const { data } = await apiClient.post<SyncOfflineResultado>(`${BASE}/sync-offline`, { vendas });
    return data;
  },
};
